import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Pencil, Upload, KeyRound, Settings, Activity, ArchiveRestore, CheckCircle2, Mail, Database } from 'lucide-react';
import { api } from '../../lib/api';

/* Collection names → Turkish labels */
const COLL_TR = {
  settings: 'Ayar', hero: 'Kahraman Slaytı', news: 'Duyuru', menu: 'Menü', page: 'Özel Sayfa',
  sponsors: 'Destekçi', presidents: 'Başkan', board: 'Yönetim Kurulu Üyesi', events: 'Etkinlik',
  press: 'Basın Haberi', bank_accounts: 'Banka Hesabı', special_days: 'Özel Gün', faq: 'SSS',
  projects: 'Proje', milestones: 'Tarihçe Maddesi', stories: 'Bursiyer Hikâyesi',
  volunteer_fields_bireysel: 'Bireysel Form Alanı', volunteer_fields_kurumsal: 'Kurumsal Form Alanı',
  units: 'Birim', press_clips: 'Basın Kupürü', videos: 'Video', about_blocks: 'Hakkımızda Bloğu',
  users: 'Yönetici Kullanıcı', upload: 'Dosya', donation: 'Bağış', volunteer: 'Gönüllü Başvurusu',
  backup: 'Yedek', email: 'E-posta',
};

const STATUS_TR = {
  completed: 'Onaylandı', pending: 'Onay Bekliyor', rejected: 'Reddedildi',
  new: 'Yeni', approved: 'Onaylandı',
};

/* verb → badge config */
const VERBS = {
  create: { label: 'Ekleme', cls: 'bg-emerald-50 text-emerald-600', Icon: Plus },
  update: { label: 'Güncelleme', cls: 'bg-blue-50 text-blue-600', Icon: Pencil },
  delete: { label: 'Silme', cls: 'bg-red-50 text-red-600', Icon: Trash2 },
  sil: { label: 'Silme', cls: 'bg-red-50 text-red-600', Icon: Trash2 },
  upload: { label: 'Yükleme', cls: 'bg-gold/15 text-gold-500', Icon: Upload },
  status: { label: 'Durum', cls: 'bg-purple-50 text-purple-600', Icon: CheckCircle2 },
  password: { label: 'Şifre', cls: 'bg-purple-50 text-purple-600', Icon: KeyRound },
  restore: { label: 'Geri Alma', cls: 'bg-emerald-50 text-emerald-600', Icon: ArchiveRestore },
  other: { label: 'İşlem', cls: 'bg-ink-50 text-ink-600', Icon: Activity },
};

/** Parse a raw log into { verb, badge, description }. */
function describeLog(l) {
  const a = l.action || '';
  const m = l.meta || {};
  const label = m.label ? `"${m.label}"` : (m.id ? `(#${String(m.id).slice(0, 8)})` : '');

  // Settings — richest info
  if (a === 'settings.update' || a === 'ayarlar.güncelleme') {
    const key = m.key || '?';
    if (m.is_new) return { verb: 'create', text: <>"<b>{key}</b>" ayarını ekledi{m.new ? <> · yeni değer: <i>"{m.new}"</i></> : null}</> };
    if (m.old !== undefined && m.old !== null && String(m.old) !== String(m.new ?? '')) {
      return { verb: 'update', text: <>"<b>{key}</b>" ayarını değiştirdi · <span className="text-red-500 line-through">"{String(m.old) || '(boş)'}"</span> → <span className="text-emerald-600">"{String(m.new ?? '')}"</span></> };
    }
    return { verb: 'update', text: <>"<b>{key}</b>" ayarını güncelledi{m.new !== undefined ? <> · değer: <i>"{String(m.new ?? '')}"</i></> : null}</> };
  }

  // Uploads
  if (a === 'upload' || a === 'yüklemek') return { verb: 'upload', text: <>Dosya yükledi: <b>{m.orig || m.name}</b>{m.size_mb ? ` (${m.size_mb} MB)` : ''}</> };
  if (a === 'upload.delete' || a === 'yükle.sil') return { verb: 'delete', text: <>Dosyayı sildi: <b>{m.name}</b></> };
  if (a === 'upload.update') return { verb: 'update', text: <>Dosya bilgisini güncelledi: <b>{m.name}</b></> };

  // Users
  if (a === 'users.create') return { verb: 'create', text: <>Yeni yönetici ekledi: <b>{m.username}</b>{m.role ? ` (${m.role})` : ''}</> };
  if (a === 'users.delete') return { verb: 'delete', text: <>Yöneticiyi sildi: <b>{m.username}</b></> };
  if (a === 'users.update') return { verb: 'update', text: <>Yönetici bilgilerini güncelledi{m.fields?.length ? <> · alanlar: <i>{m.fields.join(', ')}</i></> : null}</> };
  if (a === 'users.change-password') return { verb: 'password', text: <>Kendi şifresini değiştirdi</> };

  // Donations
  const whoBit = (mm) => mm.who ? <> · <b>{mm.who}</b>{mm.email ? <span className="text-ink-400"> ({mm.email})</span> : null}{mm.amount ? <> · <b>₺{Number(mm.amount).toLocaleString('tr-TR')}</b></> : null}{mm.method ? ` · ${mm.method === 'havale' ? 'Havale/EFT' : 'Kredi Kartı'}` : ''}</> : (m.id ? <> (#{String(m.id).slice(0, 8)})</> : null);
  if (a === 'donation_status') return { verb: 'status', text: <>Bağış durumunu değiştirdi: <b>{STATUS_TR[m.status] || m.status}</b>{whoBit(m)}</> };
  if (a === 'donation_delete') return { verb: 'delete', text: <>Bağış kaydını sildi{whoBit(m)}</> };

  // Volunteers
  const volBit = (mm) => mm.who || mm.email ? <> · <b>{mm.who || '(isimsiz)'}</b>{mm.email ? <span className="text-ink-400"> ({mm.email})</span> : null}{mm.type ? ` · ${mm.type}` : ''}</> : (m.id ? <> (#{String(m.id).slice(0, 8)})</> : null);
  if (a === 'volunteer_status') return { verb: 'status', text: <>Başvuru durumunu değiştirdi: <b>{STATUS_TR[m.status] || m.status}</b>{volBit(m)}</> };
  if (a === 'volunteer_trash') return { verb: 'delete', text: <>Başvuruyu çöp kutusuna taşıdı{volBit(m)}</> };
  if (a === 'volunteer_restore') return { verb: 'restore', text: <>Başvuruyu çöp kutusundan geri aldı{volBit(m)}</> };
  if (a === 'volunteer_purge') return { verb: 'delete', text: <>Başvuruyu kalıcı olarak sildi{volBit(m)}</> };

  // Backups
  if (a.startsWith('backup.')) {
    const sub = a.split('.').slice(1).join('.');
    const map = {
      'snapshot': { verb: 'create', t: <>Yedek oluşturdu: <b>{m.name}</b></> },
      'export': { verb: 'other', t: <>Yedek dışa aktardı</> },
      'restore': { verb: 'restore', t: <>Yedekten geri yükledi</> },
      'restore-upload': { verb: 'restore', t: <>Yüklenen dosyadan geri yükledi</> },
      'trash': { verb: 'delete', t: <>Yedeği çöp kutusuna taşıdı: <b>{m.name}</b></> },
      'trash.restore': { verb: 'restore', t: <>Yedeği çöpten geri aldı: <b>{m.trash_name}</b></> },
      'trash.purge': { verb: 'delete', t: <>Yedeği kalıcı sildi: <b>{m.name}</b></> },
      'trash.empty': { verb: 'delete', t: <>Yedek çöp kutusunu boşalttı ({m.removed} adet)</> },
    };
    const hit = map[sub];
    if (hit) return { verb: hit.verb, text: hit.t };
  }

  if (a === 'email.test') return { verb: 'other', text: <>Test e-postası gönderdi: <b>{m.to}</b> ({m.sent ? 'başarılı' : 'başarısız'})</> };
  if (a === 'login.failed') return { verb: 'other', text: <>⚠️ Başarısız giriş denemesi{m.ip ? <> · IP: <b>{m.ip}</b></> : null}</> };
  if (a === 'site.publish') return { verb: 'other', text: <>Siteyi yayınladı (v{m.version}) — ziyaretçi sayfaları yenilendi</> };
  if (a === 'seed-content') return { verb: 'other', text: <>Örnek içerik yükledi</> };

  // Generic "<collection>.<verb>" (sponsors.create, milestones.update, hero.delete, menu.reorder …)
  const parts = a.split('.');
  if (parts.length === 2) {
    const [coll, verb] = parts;
    const collTr = COLL_TR[coll] || coll;
    if (verb === 'create') return { verb, text: <><b>{collTr}</b> ekledi {label}</> };
    if (verb === 'update' || verb === 'güncelleme') return { verb: 'update', text: <><b>{collTr}</b> güncelledi {label}</> };
    if (verb === 'delete') return { verb, text: <><b>{collTr}</b> sildi {label}</> };
    if (verb === 'reorder') return { verb: 'update', text: <><b>{collTr}</b> sıralamasını değiştirdi{m.count ? ` (${m.count} öğe)` : ''}</> };
  }

  return { verb: 'other', text: <>{a} <span className="font-mono text-ink-400">{JSON.stringify(m)}</span></> };
}

export default function AdminLogs() {
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState('all');
  useEffect(() => { api.adminListLogs(300).then(setLogs).catch(() => setLogs([])); }, []);

  const enriched = logs.map(l => ({ ...l, d: describeLog(l) }));
  const shown = filter === 'all' ? enriched : enriched.filter(l => (VERBS[l.d.verb] || VERBS.other).label === filter);
  const FILTERS = ['all', 'Ekleme', 'Güncelleme', 'Silme', 'Yükleme', 'Durum', 'Şifre'];

  return (
    <div data-testid="admin-logs">
      <div className="mb-8">
        <div className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-bold">Admin Aktiviteleri</div>
        <h1 className="font-display text-4xl mt-2 leading-none">İşlem Geçmişi</h1>
        <p className="mt-2 text-sm text-ink-400">Kim, ne zaman, hangi işlemi yaptı — ekleme, silme, güncelleme, şifre değişikliği dahil tüm detaylar.</p>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {FILTERS.map(f => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`log-filter-${f}`}
            className={`px-4 h-9 rounded-full text-xs font-semibold border transition-colors ${filter === f ? 'bg-ink-600 text-cream border-ink-600' : 'bg-white text-ink-500 border-ink-100 hover:border-ink-300'}`}>
            {f === 'all' ? `Tümü (${enriched.length})` : f}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-ink-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-cream-200">
            <tr>
              <th className="text-left p-3 font-semibold text-ink-500 text-xs uppercase tracking-wider whitespace-nowrap">Tarih</th>
              <th className="text-left p-3 font-semibold text-ink-500 text-xs uppercase tracking-wider">Kullanıcı</th>
              <th className="text-left p-3 font-semibold text-ink-500 text-xs uppercase tracking-wider">İşlem</th>
              <th className="text-left p-3 font-semibold text-ink-500 text-xs uppercase tracking-wider">Ne Yaptı?</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((l) => {
              const badge = VERBS[l.d.verb] || VERBS.other;
              const BIcon = badge.Icon;
              return (
                <tr key={l.id} className="border-t border-ink-50 hover:bg-cream-200/40">
                  <td className="p-3 text-ink-500 tabular text-xs whitespace-nowrap">{l.created_at ? new Date(l.created_at).toLocaleString('tr-TR') : ''}</td>
                  <td className="p-3"><span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-ink-50 text-ink-600">{l.username}</span></td>
                  <td className="p-3 whitespace-nowrap">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${badge.cls}`}>
                      <BIcon className="w-3 h-3" /> {badge.label}
                    </span>
                  </td>
                  <td className="p-3 text-ink-600 text-xs leading-relaxed break-words max-w-2xl">{l.d.text}</td>
                </tr>
              );
            })}
            {shown.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-ink-400">Bu görünümde işlem yok</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
